import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
const FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'textarea:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])', 'summary', '[tabindex]:not([tabindex="-1"])',
].join(', ');
const FIELD = 'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled])';
function matchesCoarsePointer() {
    if (typeof window === 'undefined' || !window.matchMedia)
        return false;
    return window.matchMedia('(pointer: coarse)').matches;
}
function focusables(root) {
    return [...root.querySelectorAll(FOCUSABLE)].filter((el) => !el.closest('[inert]') && el.getAttribute('aria-hidden') !== 'true');
}
export function Drawer({ open, onClose, label, children, forceDialog, modal, returnFocus, className }) {
    const ref = useRef(null);
    const portalRoot = useRef(null);
    const previouslyFocused = useRef(null);
    // Keep the latest callbacks without re-running the focus effect on every
    // parent render (which would steal focus from the field being typed in).
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const returnFocusRef = useRef(returnFocus);
    returnFocusRef.current = returnFocus;
    const acts = !!modal || (forceDialog ?? (open && matchesCoarsePointer()));
    if (modal && open && typeof document !== 'undefined' && !portalRoot.current) {
        portalRoot.current = document.createElement('div');
        portalRoot.current.setAttribute('data-drawer-portal', '');
    }
    useEffect(() => {
        if (!open || !modal || !portalRoot.current)
            return;
        const root = portalRoot.current;
        document.body.appendChild(root);
        // Everything else in <body> becomes inert while the modal drawer is open.
        const siblings = [...document.body.children].filter((el) => el !== root && !el.hasAttribute('inert'));
        for (const el of siblings)
            el.setAttribute('inert', '');
        return () => {
            for (const el of siblings)
                el.removeAttribute('inert');
            root.remove();
        };
    }, [open, modal]);
    useEffect(() => {
        if (!open)
            return;
        if (!acts)
            return;
        previouslyFocused.current = document.activeElement ?? null;
        const drawer = ref.current;
        const firstField = modal ? drawer?.querySelector(FIELD) : null;
        (firstField ?? drawer)?.focus();
        const onKey = (e) => {
            if (e.key === 'Escape')
                onCloseRef.current();
            if (e.key === 'Tab' && ref.current) {
                const list = focusables(ref.current);
                if (list.length === 0) {
                    e.preventDefault();
                    return;
                }
                const first = list[0];
                const last = list[list.length - 1];
                const active = document.activeElement;
                const inside = !!active && ref.current.contains(active) && active !== ref.current;
                if (e.shiftKey && (active === first || !inside)) {
                    e.preventDefault();
                    last.focus();
                }
                else if (!e.shiftKey && (active === last || !inside)) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('keydown', onKey);
            const back = previouslyFocused.current;
            // Restore after the inert attributes are lifted (same tick, later cleanup).
            queueMicrotask(() => {
                if (back && back.isConnected)
                    back.focus?.();
                else
                    returnFocusRef.current?.()?.focus?.();
            });
        };
    }, [open, acts, modal]);
    // V98-3-7 [R3] keyboard-safe: while the drawer acts as a dialog, its bottom edge rides above the on-screen
    // keyboard (visualViewport), so the field being typed in and the actions never sit under it.
    useEffect(() => {
        const vv = typeof window !== 'undefined' ? window.visualViewport : null;
        const el = ref.current;
        if (!open || !acts || !vv || !el)
            return;
        const update = () => {
            const covered = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
            el.style.bottom = `${covered}px`;
            el.setAttribute('data-keyboard', covered > 0 ? 'open' : 'closed');
        };
        update();
        vv.addEventListener('resize', update);
        vv.addEventListener('scroll', update);
        return () => { vv.removeEventListener('resize', update); vv.removeEventListener('scroll', update); };
    }, [open, acts]);
    if (!open)
        return null;
    // Dialog semantics ONLY when acts=true (mobile, forceDialog or modal).
    const dialogProps = acts
        ? {
            role: 'dialog',
            'aria-modal': true,
            tabIndex: -1,
        }
        : {
            role: undefined,
            tabIndex: undefined,
        };
    const aside = (_jsx("aside", { ref: ref, className: className, "data-drawer": true, "data-mode": acts ? 'dialog' : 'aside', "aria-label": label, ...dialogProps, style: {
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: 'min(420px, 100vw)',
            background: 'var(--surface)',
            borderLeft: '1px solid var(--border)',
            boxShadow: 'var(--elevation-md)',
            padding: 'var(--space-6)',
            paddingBottom: modal ? 'calc(var(--space-6) + env(safe-area-inset-bottom, 0px))' : 'var(--space-6)',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            zIndex: modal ? 61 : 40,
        }, children: children }));
    if (!modal)
        return aside;
    if (!portalRoot.current)
        return null;
    return createPortal(_jsxs(_Fragment, { children: [_jsx("div", { "data-drawer-backdrop": true, "aria-hidden": "true", onClick: () => onCloseRef.current(), style: { position: 'fixed', inset: 0, background: 'var(--overlay-bg)', zIndex: 60 } }), aside] }), portalRoot.current);
}
//# sourceMappingURL=Drawer.js.map